// EasyAuth (Azure App Service Authentication) login gate.
//
// Context: On staff-gated environments (e.g. prodsup and prod), EasyAuth is set to
// "Require authentication -> Return HTTP 401" so the portal stays locked to Entra-authenticated
// staff. A 401 stops the CORS error (it is a same-origin response), but it does NOT log anyone
// in: the SPA's startup `fetch` calls simply come back 401 and the app renders broken.
//
// This helper detects that "not signed in to EasyAuth" state and drives the login as a
// FULL-PAGE browser navigation to `/.auth/login/aad`. A full-page navigation is not subject to
// CORS, so Entra shows its login page normally, sets the `AppServiceAuthSession` cookie, and
// returns the user here authenticated. Subsequent `fetch` calls then carry the cookie.
//
// Safety properties:
//  - Env-scoped: only runs when REACT_APP_ENTRA_GATE_ENABLED === "true". Disabled by default.
//  - Auth-source specific: probes EasyAuth's own `/.auth/me` identity endpoint, NOT an `/api/*`
//    endpoint, so it can never be confused with the app's own SAML/authorization 401s.
//  - Fail-closed: the gate is governed by the GATE_ENABLED flag, not by whether EasyAuth
//    happens to be reachable. When gated, an unreachable or failed `/.auth/me` probe still
//    drives the login rather than exposing the app.

const GATE_ENABLED = process.env.REACT_APP_ENTRA_GATE_ENABLED === "true";

// EasyAuth endpoints are always served on the current host and are excluded from the app's
// auth requirement, so relative paths keep the probe same-origin (EasyAuth is per-host).
const AUTH_ME_PATH = "/.auth/me";
const LOGIN_PATH = "/.auth/login/aad";

/**
 * Drives the EasyAuth login as a full-page navigation (not subject to CORS). Returns `false` so
 * the caller stops bootstrapping while the redirect is in flight. Relies on the App Service
 * EasyAuth reply-URL/host being bound to the browsing host so the session cookie sticks on the
 * first redirect.
 */
function redirectToEasyAuthLogin(): false {
  const returnUri = encodeURIComponent(window.location.href);
  window.location.replace(`${LOGIN_PATH}?post_login_redirect_uri=${returnUri}`);
  return false;
}

/**
 * Returns true when the current user has an EasyAuth (Entra) session on this host.
 * Throws when `/.auth/me` is absent/unreachable so the caller can fail closed (redirect to login).
 */
async function isEasyAuthAuthenticated(): Promise<boolean> {
  const response = await fetch(AUTH_ME_PATH, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
    // Detect an IdP redirect without following it cross-origin (which would throw a CORS error).
    redirect: "manual",
  });

  // A challenge (401) or an IdP redirect (opaqueredirect / status 0) means "not signed in".
  if (
    response.status === 401 ||
    response.type === "opaqueredirect" ||
    response.status === 0
  ) {
    return false;
  }

  // Any other non-OK status (e.g. 404 when EasyAuth is not enabled) -> surface to the caller,
  // which fails closed when the environment is gated.
  if (!response.ok) {
    throw new Error(`Unexpected ${AUTH_ME_PATH} status ${response.status}`);
  }

  // `/.auth/me` returns an array of identities; empty means no active session.
  const identities = await response.json().catch(() => []);
  return Array.isArray(identities) && identities.length > 0;
}

/**
 * Ensures the user is authenticated via EasyAuth before the app bootstraps.
 *
 * @returns `true` when bootstrap should continue (gate disabled or already authenticated);
 *          `false` when a full-page login redirect is in progress and the caller should stop.
 */
export async function ensureEntraAuthenticated(): Promise<boolean> {
  // Only active on explicitly staff-gated environments.
  if (!GATE_ENABLED) {
    return true;
  }

  let authenticated: boolean;
  try {
    authenticated = await isEasyAuthAuthenticated();
  } catch (error) {
    // The environment is explicitly gated (GATE_ENABLED === true), so a failed or unreachable
    // probe must NOT expose the app. Fail closed by driving the login rather than rendering.
    console.warn("EasyAuth gate probe failed; redirecting to login.", error);
    return redirectToEasyAuthLogin();
  }

  if (authenticated) {
    return true;
  }

  // Not signed in: drive login as a full-page navigation (not subject to CORS).
  return redirectToEasyAuthLogin();
}
